import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Link, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import AddIcon from '@mui/icons-material/Add'
import PaperDraggable from "../../../../../components/PaperDraggable";
import { useCallback, useContext, useState } from "react";
import DialogSelecionarProduto from "./DialogSelecionarProduto";
import { FormProvider, useForm } from "react-hook-form";
import moment from "moment";
import { AuthContext } from "../../../../../contexts/AuthContext";
import DeleteIcon from "@mui/icons-material/Delete";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import ConfirmDialog from "../../../../../components/BasicComponents/Dialogs/ConfirmDialog";
import { useNavigate } from "react-router-dom";
import { InputsSelecionarProduto } from "../../../../../components/BasicComponents/SelecionarProduto";
import { formatarInputValorMonetárioSemPrefixo, formatarNumeroComSeparadorMilhar } from "../../../../../helpers/masks";

export default function DialogSolicitarCompraProduto({
    open,
    onClose
}: DialogSolicitarCompraProdutoProps) {

    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { userPerfilComLoja } = useContext(AuthContext)

    const [openSelectProductDialog, setOpenSelectProductDialog] = useState(false);
    const [openModalConfirmacao, setOpenModalConfirmacao] = useState(false);

    const { mutate: criarSolicitacao, isLoading: isLoadingCreate } = useMutation({
        mutationFn: (data: TCriarSolicitacao) =>
            axiosClient.post('/estoque/compras/criar-solicitacao', data),
        onError: (error) => {
            console.error(error);
            enqueueSnackbar("Erro ao criar solicitação", { variant: "error" });
        },
        onSuccess: (response) => {
            queryClient.invalidateQueries('listar-solicitacoes-compras');
            onClose();
            setOpenModalConfirmacao(false);
            const id = response.data?.codigo;
            navigate(`/estoque/compras/listar-solicitacoes?detalhe-solicitacao=${id}`);
            enqueueSnackbar("Solicitação criada com sucesso", { variant: "success" });
        },
    });

    const methods = useForm<InputsSelecionarProduto>({
        defaultValues: {
            items: [],
            descricao: "",
            observacao: "",
            nao_sugeridos: [],
            filtros: {
                origem: null
            },
            filters: {
                limit: 50,
                page: 0,
                palavra_chave: "",
                periodo_vendas_inicio: moment().subtract(1, 'day').subtract(1, 'year'),
                periodo_vendas_fim: moment().subtract(1, 'day'),
                sazonalidade_inicio: moment().subtract(1, 'day').subtract(1, 'year'),
                sazonalidade_fim: moment().subtract(1, 'day').subtract(1, 'year').add('3', 'months'),
                sazonalidades_ids: null,
                qtdSuggestion: 30,
                origem: null,
                destino: userPerfilComLoja?.loja || null,
                predSelecionado: null,
                mostrar_itens_sem_estoque_origem: true,
                niveis_estoque: ["0a15", "16a30"],
                calcular_nivel_global: true,
            }
        }
    })

    const { getValues } = methods;


    // Funções manipuladoras
    const handleDelete = (codigo: number) => {
        const items = getValues('items');
        const updatedItems = items.filter(item => item.codigo !== codigo);
        methods.setValue('items', updatedItems);
    }


    const handleConfirmar = useCallback(() => {
        const { items, observacao, filters } = getValues();

        const params: TCriarSolicitacao = {
            itens: items.map(item => ({
                produto_id: Number(item.codigo),
                nome: item.nome,
                quantidade: item.quantidade,
                observacao: item.observacao,
            })),
            observacoes: observacao,
            loja_id: filters.destino.id ?? 0
        };

        criarSolicitacao(params)
    },
        [criarSolicitacao, getValues]
    );
    //

    return (
        <FormProvider {...methods} >
            <Dialog
                open={open}
                maxWidth="lg"
                fullWidth
                PaperComponent={PaperDraggable}
                aria-labelledby="draggable-dialog-title"
            >

                <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">Criar nova solicitação</DialogTitle>
                <DialogContent>
                    <Typography variant="subtitle2" color="textSecondary">Preencha os detalhes abaixo para criar uma nova solicitação de produtos. Adicione os itens desejados e informe as quantidades necessárias.</Typography>

                    <Stack my={2} direction={'row'} justifyContent={'space-between'} alignItems={'center'} spacing={2}>
                        <Typography variant="body1" fontWeight={'bold'}>Produtos</Typography>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={() => setOpenSelectProductDialog(true)}
                            variant="outlined"
                        >
                            Adicionar produto
                        </Button>
                    </Stack>
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell><b>Nome</b></TableCell>
                                    <TableCell><b>Estoque total</b></TableCell>
                                    <TableCell><b>Quantidade solicitada</b></TableCell>
                                    <TableCell><b>Observações</b></TableCell>
                                    <TableCell><b>Ações</b></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                <TableRow>
                                    {
                                        getValues('items').length === 0 &&
                                        <TableCell colSpan={5} align="center">Sua solicitação está vazia no momento. Comece clicando em “Adicionar produto” ou no {
                                            <Link
                                                onClick={() => setOpenSelectProductDialog(true)}
                                                sx={{ cursor: 'pointer' }}>
                                                link
                                            </Link>
                                        } aqui para escolher os itens desejados.</TableCell>
                                    }
                                </TableRow>
                                {
                                    getValues('items').length > 0 &&
                                    getValues('items').map((item, index) => {

                                        return (
                                            <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell>{item.nome}</TableCell>
                                                <TableCell>{formatarNumeroComSeparadorMilhar(item.estoque_total)} {item.unidade_medida}</TableCell>
                                                <TableCell>{formatarNumeroComSeparadorMilhar(item.quantidade)} {item.unidade_medida}</TableCell>
                                                <TableCell>{item.observacao ?? 'N/A'}</TableCell>
                                                <TableCell>
                                                    <IconButton
                                                        aria-label="deletar"
                                                        onClick={() => handleDelete(item.codigo)}
                                                        title="Deletar"
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions>

                    <Button onClick={onClose} variant="outlined">Fechar</Button>
                    <Button
                        disabled={getValues('items').length === 0 || isLoadingCreate}
                        variant="contained"
                        onClick={() => setOpenModalConfirmacao(true)}
                    >
                        Criar solicitação
                    </Button>
                </DialogActions>
            </Dialog>
            {
                openSelectProductDialog &&
                <DialogSelecionarProduto
                    open={openSelectProductDialog}
                    onClose={() => setOpenSelectProductDialog(false)}
                />
            }
            {
                openModalConfirmacao &&
                <ConfirmDialog
                    title="Criar solicitação"
                    message="Deseja realmente criar a solicitação?"
                    cancelText="Cancelar"
                    confirmText="Criar"
                    open={openModalConfirmacao}
                    onClose={() => setOpenModalConfirmacao(false)}
                    onConfirm={handleConfirmar}
                    disabled={isLoadingCreate}
                />
            }
        </FormProvider>
    )
}

interface DialogSolicitarCompraProdutoProps {
    open: boolean;
    onClose: () => void;
}

type TCriarSolicitacao = {
    itens: Array<{
        produto_id: number;
        nome: string;
        quantidade: number;
        observacao: string | null;
    }>
    observacoes: string | null;
    loja_id: number;
}
